<template>
  <div>
    <div class="container">
      <div class="box-title">
        <h4><i class="fal fa-balance-scale"></i> ຍອດດຸ່ນດ່ຽງກະເປົ໋າ (Balance)</h4>
        <vs-button flat size="small" @click="exportExcel" :loading="isExporting" :disabled="isExporting">
          <i class="fal fa-file-export"></i> Export Excel
        </vs-button>
      </div>

      <!-- Tabs -->
      <div class="tabs">
        <ul>
          <li :class="{ 'is-active': tab === 'employee' }"><a @click="switchTab('employee')"><i class="fal fa-user"></i> ຕາມພະນັກງານ</a></li>
          <li :class="{ 'is-active': tab === 'branch' }"><a @click="switchTab('branch')"><i class="fal fa-store"></i> ຕາມສາຂາ</a></li>
        </ul>
      </div>

      <!-- Filters -->
      <div class="box">
        <div class="columns">
          <div class="column is-4">
            <label class="label">ສາຂາ</label>
            <BranchSelect v-model="filters.branch_id" placeholder="ທັງໝົດ" />
          </div>
          <div class="column is-3">
            <label class="label">ແຕ່ວັນທີ</label>
            <input type="date" class="input" v-model="filters.from_date" />
          </div>
          <div class="column is-3">
            <label class="label">ຫາວັນທີ</label>
            <input type="date" class="input" v-model="filters.to_date" />
          </div>
          <div class="column is-2 apply-col">
            <vs-button @click="apply" :loading="isLoading"><i class="fal fa-search"></i> ຄົ້ນຫາ</vs-button>
          </div>
        </div>
        <p class="cap-hint">* ໄລຍະວັນທີສູງສຸດ 31 ວັນ</p>
      </div>

      <!-- Processing (warm-cache poll) -->
      <div v-if="status === 'processing'" class="box notification is-info processing">
        <i class="fal fa-spinner fa-spin"></i>
        ກຳລັງປະມວນຜົນຍອດ... ລະບົບກຳລັງຄຳນວນ, ໜ້ານີ້ຈະອັບເດດເອງເມື່ອພ້ອມ.
      </div>

      <div v-if="error" class="box notification is-warning">
        <p>{{ error }}</p>
        <vs-button @click="fetch" class="mt-3"><i class="fal fa-redo"></i> ລອງໃໝ່</vs-button>
      </div>

      <!-- Branch live balance overlay (single focused branch only) -->
      <div v-if="tab === 'branch' && !allBranchMode && status === 'ready'" class="box">
        <h5 class="section-title">ຍອດເງິນ live (ລະບົບກະເປົ໋າ)</h5>
        <div v-if="liveWallets.length" class="live-wrap">
          <div v-for="w in liveWallets" :key="w.wallet_type || w.id" class="live-row">
            <span class="tag" :class="(w.wallet_type === 'COD') ? 'is-warning' : 'is-info'">{{ walletLabel(w.wallet_type) }}</span>
            <span class="live-bal">{{ formatCurrency(w.balance) }}</span>
            <span v-if="w.available != null" class="live-avail">ໃຊ້ໄດ້: {{ formatCurrency(w.available) }}</span>
          </div>
        </div>
        <p v-else class="has-text-grey">
          {{ data && data.wallet_service_available === false ? 'ບໍ່ສາມາດເຊື່ອມຕໍ່ລະບົບກະເປົ໋າ (ສະແດງສະເພາະຍອດ flow)' : 'ບໍ່ມີຂໍ້ມູນຍອດ live' }}
        </p>
      </div>

      <!-- Employee table -->
      <div v-if="tab === 'employee' && status === 'ready'" class="box">
        <h5 class="section-title">ຍອດສຸດທິຕາມພະນັກງານ ({{ employeeRows.length }})</h5>
        <table class="table is-fullwidth is-hoverable is-striped">
          <thead>
            <tr>
              <th>ພະນັກງານ</th>
              <th class="num">ຄ່າຂົນສົ່ງຕົ້ນທາງ</th>
              <th class="num">ຄ່າຂົນສົ່ງປາຍທາງ</th>
              <th class="num">COD</th>
              <th class="num">ຄືນເງິນ ຄ່າຂົນສົ່ງ</th>
              <th class="num">ຄືນເງິນ COD</th>
              <th class="num">ປັບຍອດ ຄ່າຂົນສົ່ງ</th>
              <th class="num">ປັບຍອດ COD</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in employeeRows" :key="row.operator_id || row.operator_name">
              <td>{{ row.operator_name }}</td>
              <td class="num">{{ formatCurrency(row.origin) }}</td>
              <td class="num">{{ formatCurrency(row.destination) }}</td>
              <td class="num">{{ formatCurrency(row.cod) }}</td>
              <td class="num has-text-danger">{{ formatCurrency(row.refundFreight) }}</td>
              <td class="num has-text-danger">{{ formatCurrency(row.refundCod) }}</td>
              <td class="num">{{ formatCurrency(row.adjFreight) }}</td>
              <td class="num">{{ formatCurrency(row.adjCod) }}</td>
            </tr>
          </tbody>
          <tfoot v-if="employeeRows.length">
            <tr class="totals">
              <th>ລວມ</th>
              <th class="num">{{ formatCurrency(totals.origin) }}</th>
              <th class="num">{{ formatCurrency(totals.destination) }}</th>
              <th class="num">{{ formatCurrency(totals.cod) }}</th>
              <th class="num">{{ formatCurrency(totals.refundFreight) }}</th>
              <th class="num">{{ formatCurrency(totals.refundCod) }}</th>
              <th class="num">{{ formatCurrency(totals.adjFreight) }}</th>
              <th class="num">{{ formatCurrency(totals.adjCod) }}</th>
            </tr>
          </tfoot>
        </table>
        <p v-if="!employeeRows.length" class="has-text-centered has-text-grey py-3">ບໍ່ມີຂໍ້ມູນ</p>
      </div>

      <!-- Branch buckets table -->
      <div v-if="tab === 'branch' && !allBranchMode && status === 'ready' && data && data.buckets" class="box">
        <h5 class="section-title">ຍອດ flow ຂອງສາຂາ (SWT)</h5>
        <table class="table is-fullwidth is-striped">
          <thead><tr><th>ປະເພດ</th><th class="num">ຄ່າຂົນສົ່ງ</th><th class="num">COD</th></tr></thead>
          <tbody>
            <tr v-for="r in branchRows" :key="r.key"><td>{{ r.label }}</td>
              <td class="num">{{ r.freight == null ? '-' : formatCurrency(r.freight) }}</td>
              <td class="num">{{ r.cod == null ? '-' : formatCurrency(r.cod) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- All-branches table (one row per branch: live balance + SWT flow buckets) -->
      <div v-if="allBranchMode && status === 'ready'" class="box">
        <h5 class="section-title">ຍອດຕາມສາຂາ ({{ allBranchRows.length }})</h5>
        <p v-if="data && data.wallet_service_available === false" class="cap-hint">
          * ຍອດ live ບໍ່ພ້ອມ (ລະບົບກະເປົ໋າຂັດຂ້ອງ) — ສະແດງສະເພາະຍອດ flow
        </p>
        <table class="table is-fullwidth is-hoverable is-striped">
          <thead>
            <tr>
              <th>ສາຂາ</th>
              <th class="num">ຍອດ live ຄ່າຂົນສົ່ງ</th>
              <th class="num">ຍອດ live COD</th>
              <th class="num">ຄ່າຂົນສົ່ງຕົ້ນທາງ</th>
              <th class="num">ຄ່າຂົນສົ່ງປາຍທາງ</th>
              <th class="num">COD</th>
              <th class="num">ຄືນເງິນ ຄ່າຂົນສົ່ງ</th>
              <th class="num">ຄືນເງິນ COD</th>
              <th class="num">ປັບຍອດ ຄ່າຂົນສົ່ງ</th>
              <th class="num">ປັບຍອດ COD</th>
              <th class="num">ອື່ນໆ ຄ່າຂົນສົ່ງ</th>
              <th class="num">ອື່ນໆ COD</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in allBranchRows" :key="row.branch_id">
              <td>{{ row.label }}</td>
              <td class="num live-bal">{{ row.liveFreight == null ? '-' : formatCurrency(row.liveFreight) }}</td>
              <td class="num live-bal">{{ row.liveCod == null ? '-' : formatCurrency(row.liveCod) }}</td>
              <td class="num">{{ formatCurrency(row.origin) }}</td>
              <td class="num">{{ formatCurrency(row.destination) }}</td>
              <td class="num">{{ formatCurrency(row.cod) }}</td>
              <td class="num has-text-danger">{{ formatCurrency(row.refundFreight) }}</td>
              <td class="num has-text-danger">{{ formatCurrency(row.refundCod) }}</td>
              <td class="num">{{ formatCurrency(row.adjFreight) }}</td>
              <td class="num">{{ formatCurrency(row.adjCod) }}</td>
              <td class="num">{{ formatCurrency(row.otherFreight) }}</td>
              <td class="num">{{ formatCurrency(row.otherCod) }}</td>
            </tr>
          </tbody>
        </table>
        <p v-if="!allBranchRows.length" class="has-text-centered has-text-grey py-3">ບໍ່ມີຂໍ້ມູນ</p>
      </div>
    </div>
  </div>
</template>

<script>
import walletMonitorService from '@/services/walletMonitorService';
import { extractErrorInfo } from '@/services/walletService';
import BranchSelect from '@/components/BranchSelect.vue';
import { formatCurrency } from '@/services/walletFormatters';
import { normalizeLiveWallets, walletLabel, todayStr } from '@/services/walletMonitorLabels';

const POLL_MS = 5000;
const MAX_POLLS = 24; // ~2 minutes

// Async export tuning — mirrors Monitor/Transactions.vue (the BFF caps the range at 92 days).
const EXPORT_REFRESH_MS = 6000;
const EXPORT_MAX_POLLS = 40;      // ~4 min of bounded polling, then stop
const EXPORT_MAX_RANGE_DAYS = 92;

function amtQtyNet(b) { return (b && b.net && Number(b.net.total)) || 0; }
function successNet(split) {
  if (!split) return 0;
  return amtQtyNet(split.normal) + amtQtyNet(split.return) + amtQtyNet(split.none);
}
// Net of ONE wallet leg ('freight' | 'cod') of a refunds bucket — keeps FREIGHT and COD strictly separate.
function refundsLeg(refunds, leg) {
  if (!refunds || !refunds[leg]) return 0;
  const fams = refunds[leg];
  return Object.keys(fams).reduce((s, f) => s + amtQtyNet(fams[f]), 0);
}
// Net of ONE wallet leg of an adjustment / other_charge wallet-split bucket.
function legNet(ws, leg) { return (ws && ws[leg]) ? amtQtyNet(ws[leg]) : 0; }

export default {
  name: 'WalletMonitorBalance',
  components: { BranchSelect },
  data() {
    return {
      tab: 'employee',
      data: null,
      status: null,
      isLoading: false,
      error: null,
      pollTimer: null,
      pollCount: 0,
      reqId: 0,      // monotonically increasing token; a response whose token is stale is ignored
      alive: true,   // false after destroy, so a late response/poll never touches a gone component
      filters: { branch_id: '', from_date: '', to_date: '' },
      isExporting: false,
      exportPollTimer: null,
      exportPollCount: 0,
    };
  },
  computed: {
    employeeRows() {
      const emps = (this.data && this.data.employees) || [];
      return emps.map((e) => {
        const b = e.buckets || {};
        return {
          operator_id: e.operator_id,
          operator_name: e.operator_name || '—',
          origin: amtQtyNet(b.origin_charge),            // FREIGHT only
          destination: successNet(b.destination_charge), // FREIGHT only
          cod: successNet(b.cod_charge),                 // COD only
          // refunds + adjustments carry BOTH money types → split per leg, never merged (FREIGHT ≠ COD).
          refundFreight: refundsLeg(b.refunds, 'freight'),
          refundCod: refundsLeg(b.refunds, 'cod'),
          adjFreight: legNet(b.adjustment_increase, 'freight') + legNet(b.adjustment_decrease, 'freight'),
          adjCod: legNet(b.adjustment_increase, 'cod') + legNet(b.adjustment_decrease, 'cod'),
        };
      });
    },
    totals() {
      const t = { origin: 0, destination: 0, cod: 0, refundFreight: 0, refundCod: 0, adjFreight: 0, adjCod: 0 };
      this.employeeRows.forEach((r) => { Object.keys(t).forEach((k) => { t[k] += r[k]; }); });
      return t;
    },
    branchRows() {
      const b = (this.data && this.data.buckets) || {};
      // destination/cod are FREIGHT/COD single-type by construction; sum their success groups for that type.
      return [
        { key: 'origin', label: 'ຄ່າຂົນສົ່ງຕົ້ນທາງ', freight: amtQtyNet(b.origin_charge), cod: null },
        { key: 'destination', label: 'ຄ່າຂົນສົ່ງປາຍທາງ', freight: successNet(b.destination_charge), cod: null },
        { key: 'cod', label: 'COD', freight: null, cod: successNet(b.cod_charge) },
        { key: 'refunds', label: 'ຄືນເງິນ', freight: refundsLeg(b.refunds, 'freight'), cod: refundsLeg(b.refunds, 'cod') },
        { key: 'adj_inc', label: 'ປັບເພີ່ມ', freight: legNet(b.adjustment_increase, 'freight'), cod: legNet(b.adjustment_increase, 'cod') },
        { key: 'adj_dec', label: 'ປັບຫຼຸດ', freight: legNet(b.adjustment_decrease, 'freight'), cod: legNet(b.adjustment_decrease, 'cod') },
        { key: 'other', label: 'ອື່ນໆ', freight: legNet(b.other_charge, 'freight'), cod: legNet(b.other_charge, 'cod') },
      ];
    },
    liveWallets() {
      return normalizeLiveWallets(this.data && this.data.live_balance);
    },
    // All-branches mode = the branch tab with no single branch focused; backend returns data.branches[].
    allBranchMode() {
      return this.tab === 'branch' && this.data && Array.isArray(this.data.branches);
    },
    // One row per branch: name + live FREIGHT/COD balance + the same flow buckets as the single-branch view.
    allBranchRows() {
      if (!this.allBranchMode) return [];
      return (this.data.branches || []).map((br) => {
        const b = br.buckets || {};
        const live = normalizeLiveWallets(br.live_balance);
        const freight = live.find((w) => String(w.wallet_type).toUpperCase() === 'FREIGHT');
        const cod = live.find((w) => String(w.wallet_type).toUpperCase() === 'COD');
        return {
          branch_id: br.branch_id,
          label: (br.branch_prefix ? `${br.branch_prefix} - ` : '') + (br.branch_name || `#${br.branch_id}`),
          liveFreight: freight && freight.balance != null ? Number(freight.balance) : null,
          liveCod: cod && cod.balance != null ? Number(cod.balance) : null,
          origin: amtQtyNet(b.origin_charge),
          destination: successNet(b.destination_charge),
          cod: successNet(b.cod_charge),
          refundFreight: refundsLeg(b.refunds, 'freight'),
          refundCod: refundsLeg(b.refunds, 'cod'),
          adjFreight: legNet(b.adjustment_increase, 'freight') + legNet(b.adjustment_decrease, 'freight'),
          adjCod: legNet(b.adjustment_increase, 'cod') + legNet(b.adjustment_decrease, 'cod'),
          otherFreight: legNet(b.other_charge, 'freight'),
          otherCod: legNet(b.other_charge, 'cod'),
        };
      });
    },
  },
  mounted() {
    // No auto-load on open — the user drives loading via the ຄົ້ນຫາ (Search) button.
    // We still prefill today's dates so the filters are ready. The branch picker (BranchSelect)
    // loads its own options on demand via server-side search.
    this.setDefaultDates();
  },
  beforeDestroy() {
    this.alive = false;
    this.stopPolling();
    this.stopExportPoll();
  },
  methods: {
    formatCurrency, walletLabel,
    setDefaultDates() {
      this.filters.from_date = todayStr();
      this.filters.to_date = todayStr();
    },
    switchTab(tab) {
      if (this.tab === tab) return;
      this.tab = tab;
      this.stopPolling();
      this.pollCount = 0;   // a new query chain starts fresh (not inheriting the prior tab's attempts)
      this.data = null;
      this.status = null;
      this.error = null;
      // Search-driven: switching tabs only resets the view; the user reloads via the ຄົ້ນຫາ button
      // (mirrors the initial-open behaviour — no auto-load).
    },
    apply() { this.stopPolling(); this.pollCount = 0; this.fetch(); },
    validRange() {
      if (!this.filters.from_date || !this.filters.to_date) return true;
      const ms = new Date(this.filters.to_date) - new Date(this.filters.from_date);
      const days = Math.floor(ms / 86400000) + 1;
      return days >= 1 && days <= 31;
    },
    async fetch() {
      // Bump the request token FIRST — before any early return — so EVERY fetch() invalidates prior
      // in-flight responses (even a validation-failed call), and a stale response is never applied.
      const myReq = ++this.reqId;
      const myTab = this.tab;
      // Branch tab with no branch selected = all-branches table (backend returns data.branches[]).
      if (!this.validRange()) {
        this.error = 'ໄລຍະວັນທີຕ້ອງບໍ່ເກີນ 31 ວັນ';
        this.status = null;
        return;
      }
      this.isLoading = true;
      this.error = null;
      try {
        const params = { ...this.filters };
        const res = myTab === 'employee'
          ? await walletMonitorService.getEmployeeBalance(params)
          : await walletMonitorService.getBranchBalance(params);
        if (!this.alive || myReq !== this.reqId) return; // superseded — ignore
        this.data = res.data || res;
        this.status = this.data.status || 'ready';
        if (this.status === 'processing') this.schedulePoll();
        else this.stopPolling();
      } catch (error) {
        if (!this.alive || myReq !== this.reqId) return;
        const { message } = extractErrorInfo(error);
        this.error = message || 'ບໍ່ສາມາດໂຫລດຂໍ້ມູນໄດ້';
        this.status = null;
      } finally {
        if (this.alive && myReq === this.reqId) this.isLoading = false;
      }
    },
    schedulePoll() {
      this.stopPolling();
      if (!this.alive) return;
      if (this.pollCount >= MAX_POLLS) {
        this.error = 'ໃຊ້ເວລາດົນເກີນໄປ ກະລຸນາລອງໃໝ່';
        this.status = null;
        this.pollCount = 0;
        return;
      }
      // ±20% jitter so many branches polling at once don't hit the BFF in lock-step (thundering herd).
      const delay = Math.round(POLL_MS * (0.8 + Math.random() * 0.4));
      this.pollTimer = setTimeout(() => { this.pollCount += 1; this.fetch(); }, delay);
    },
    stopPolling() {
      if (this.pollTimer) { clearTimeout(this.pollTimer); this.pollTimer = null; }
      if (this.status !== 'processing') this.pollCount = 0;
    },
    // Queue the export for the ACTIVE tab (employee → balance, branch → branch_balance) with the current
    // filters, then inline-poll until the file is ready and auto-download. Mirrors Monitor/Transactions.vue.
    async exportExcel() {
      const { from_date: from, to_date: to } = this.filters;
      if (!from || !to) {
        this.$notification.OpenNotification_Error('top-right', 'warning', 3000, 'ກະລຸນາເລືອກໄລຍະວັນທີ');
        return;
      }
      const days = Math.floor((new Date(to) - new Date(from)) / 86400000) + 1;
      if (days < 1 || days > EXPORT_MAX_RANGE_DAYS) {
        this.$notification.OpenNotification_Error('top-right', 'warning', 3000, `ໄລຍະວັນທີຕ້ອງລະຫວ່າງ 1-${EXPORT_MAX_RANGE_DAYS} ວັນ`);
        return;
      }
      const reportType = this.tab === 'branch' ? 'branch_balance' : 'balance';
      this.stopExportPoll();
      this.exportPollCount = 0;
      this.isExporting = true;
      try {
        const body = await walletMonitorService.requestExport({ report_type: reportType, ...this.filters });
        const fileId = Number((body && (body.data || body)).file_id);
        if (!fileId) throw new Error('missing file_id');
        // No "queued" toast — the button spinner shows progress and the file auto-downloads when ready;
        // a success toast here is misleading (nothing is saved yet, the job is only queued).
        this.pollExport(fileId);
      } catch (error) {
        this.isExporting = false;
        // 429 = an export is already in flight for this user+type (one-at-a-time guard).
        const msg = error.response && error.response.status === 429
          ? 'ມີການສົ່ງອອກກຳລັງດຳເນີນຢູ່ ກະລຸນາລໍຖ້າ'
          : (extractErrorInfo(error).message || 'ບໍ່ສາມາດສ້າງການສົ່ງອອກ');
        this.$notification.OpenNotification_Error('top-right', 'danger', 4000, msg);
      }
    },
    pollExport(fileId) {
      this.stopExportPoll();
      // ±20% jitter so concurrent viewers don't poll in lock-step.
      const delay = Math.round(EXPORT_REFRESH_MS * (0.8 + Math.random() * 0.4));
      this.exportPollTimer = setTimeout(async () => {
        this.exportPollCount += 1;
        try {
          const result = await walletMonitorService.downloadExport(fileId);
          if (result && result.ready === false) {
            if (result.status === 'failed') {
              this.isExporting = false;
              this.$notification.OpenNotification_Error('top-right', 'danger', 4000, 'ການສົ່ງອອກລົ້ມເຫລວ');
              return;
            }
            if (this.exportPollCount >= EXPORT_MAX_POLLS) {
              this.isExporting = false;
              this.$notification.OpenNotification_Error('top-right', 'warning', 4000, 'ໃຊ້ເວລາດົນ ກະລຸນາໂຫລດຈາກໜ້າ Export');
              return;
            }
            this.pollExport(fileId); // still processing — keep polling
            return;
          }
          this.isExporting = false; // ready → downloadExport already triggered the browser download
        } catch (error) {
          this.isExporting = false;
          this.$notification.OpenNotification_Error('top-right', 'danger', 3000, extractErrorInfo(error).message || 'ບໍ່ສາມາດໂຫລດໄຟລ໌');
        }
      }, delay);
    },
    stopExportPoll() {
      if (this.exportPollTimer) { clearTimeout(this.exportPollTimer); this.exportPollTimer = null; }
    },
  },
};
</script>

<style lang="scss" scoped>
@import './date-input';
.container { margin-top: 20px; }
.box-title { margin-bottom: 16px; display: flex; align-items: center; justify-content: space-between;
  h4 { font-size: 18px; font-weight: 600; i { margin-right: 8px; color: #3273dc; } } }
.tabs { margin-bottom: 16px; ul li a { cursor: pointer; i { margin-right: 6px; } } li.is-active a { border-bottom-color: #3273dc; color: #3273dc; } }
.apply-col { display: flex; align-items: flex-end; }
.cap-hint { font-size: 12px; color: #999; margin-top: 6px; }
.processing i { margin-right: 6px; }
.mt-3 { margin-top: 1rem; }
.py-3 { padding: 1.5rem 0; }
.section-title { font-size: 15px; font-weight: 600; margin-bottom: 12px; }
/* !important: <th class="num"> (column headers + totals values) otherwise loses to Bulma's
   .table th:not([align]) default, so only <td class="num"> data cells were right-aligning. */
.num { text-align: right !important; }
.totals th { border-top: 2px solid #ddd; font-weight: 700; }
.live-wrap { display: flex; gap: 24px; flex-wrap: wrap; }
.live-row { display: flex; align-items: center; gap: 10px; }
.live-bal { font-weight: 600; }
.live-avail { color: #777; font-size: 13px; }
</style>
